import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { fetcher } from '@/utils/fetcher';
import { Edge, Node } from '@xyflow/react';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useSaveLogic } from '../../_data/useLogicData';
import { GeneralSettingsType } from '../SettingsSidebar/types';
import { useLogicValidation } from './useValidationLogic';

export interface UseHandleSaveProps {
  unitId: string;
  nodes: Node[];
  edges: Edge[];
  settings: GeneralSettingsType;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setInvalidNodeId: (id: string | null) => void;
  validateOnSave?: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSensors: {
    buttons: string[];
    buzzers: string[];
    trihows: string[];
  };
  initialStateRef: React.MutableRefObject<{
    nodes: Node[];
    edges: Edge[];
    settings: GeneralSettingsType;
    selectedSensors: {
      buttons: string[];
      buzzers: string[];
      trihows: string[];
    };
  } | null>;
}

export const useHandleSave = ({
  unitId,
  nodes,
  edges,
  settings,
  setNodes,
  setInvalidNodeId,
  setIsDirty,
  selectedSensors,
  initialStateRef,
}: UseHandleSaveProps) => {
  const toast = useToast();
  const intl = useIntl();

  // Hook that makes the API call to save the logic
  const { saveLogic, isLoading } = useSaveLogic(unitId);

  // validation logic
  const {
    validateNodeOptions,
    validateSettings,
    validateSensorToActionConnectivity,
  } = useLogicValidation();

  // save handler that gives feedback to the user
  const handleSave = useCallback(async () => {
    let hasInvalidNodes = false;
    let firstInvalidNodeError: string | null = null;
    setInvalidNodeId(null);

    const validatedNodes = nodes.map((node) => {
      const validationResult = validateNodeOptions(node, nodes, edges);
      const isValid = validationResult === true;

      if (!isValid && !hasInvalidNodes) {
        setInvalidNodeId(node.id);
        // Extract the error message from the validation result
        if (typeof validationResult === 'string') {
          firstInvalidNodeError = validationResult;
        }
      }
      if (!isValid) hasInvalidNodes = true;

      return {
        ...node,
        data: {
          ...node.data,
          isValid,
        },
      };
    });

    setNodes(validatedNodes);

    // Validate nodes and show specific error message if invalid
    if (hasInvalidNodes) {
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'Invalid Block(s)',
          id: 'emGT7r',
        }),
        subtitle:
          firstInvalidNodeError ||
          intl.formatMessage({
            defaultMessage:
              'Please check the marked blocks for missing fields and try again.',
            id: 'TCNPBK',
          }),
        time: DURATIONS.Medium,
      });
      return;
    }

    // validate Hardware settings
    const validationResult = validateSettings(settings);
    // extract invalid categories
    const invalidCategories = Object.entries(validationResult)
      .filter(([, isInvalid]) => isInvalid)
      .map(([key]) => {
        switch (key) {
          case 'controllers':
            return intl.formatMessage({
              defaultMessage: 'Controllers',
              id: 'R/onwm',
            });
          case 'nfcReaders':
            return intl.formatMessage({
              defaultMessage: 'NFC Readers',
              id: 'naF0/E',
            });
          case 'buttons':
            return intl.formatMessage({
              defaultMessage: 'Buttons',
              id: 'E2rh1p',
            });
          case 'buzzers':
            return intl.formatMessage({
              defaultMessage: 'Buzzers',
              id: '5sqpN4',
            });
          case 'leds':
            return intl.formatMessage({
              defaultMessage: 'LEDs',
              id: 'dPPyu6',
            });
          case 'screens':
            return intl.formatMessage({
              defaultMessage: 'Screens',
              id: 'iwp2S0',
            });
          default:
            return key;
        }
      });

    // If there are invalid categories, show a toast and return
    if (invalidCategories.length > 0) {
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'Invalid Hardware Settings',
          id: 'QgFm4n',
        }),
        subtitle: intl.formatMessage(
          {
            defaultMessage:
              'Please fix the configuration for the following Hardware: {invalidItems}.',
            id: 'yCklzW',
          },
          {
            invalidItems: invalidCategories.join(', '),
          },
        ),
        time: DURATIONS.Medium,
      });
      return;
    }

    // Validate that all action nodes are connected to at least one sensor node
    const unreachableNodeId = validateSensorToActionConnectivity(nodes, edges);
    if (unreachableNodeId) {
      setInvalidNodeId(unreachableNodeId);
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'Invalid logic',
          id: 'fKoX2s',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            'Please ensure all action blocks are indirectly connected to a sensor block.',
          id: '9zR+FN',
        }),
        time: DURATIONS.Medium,
      });
      return;
    }
    await saveLogic({
      settings,
      nodes: validatedNodes,
      edges,
      selectedSensors,
    });

    // Update initial state ref to reflect current state
    initialStateRef.current = {
      edges,
      nodes,
      settings,
      selectedSensors,
    };

    setIsDirty(false);

    // enable logic editor in unit settings
    try {
      await fetcher(`${RETAIL_API_URL}/units/enableLogicEditor`, {
        method: 'PUT',
        body: JSON.stringify({ unitId }),
      });
    } catch (error) {
      toast({
        title: intl.formatMessage({
          defaultMessage:
            'Error! Something went wrong enabling the Logic Editor.',
          id: 'OFw6tT',
        }),
        mode: TOASTTYPE.Danger,
      });
      console.error('Error updating Logic Editor status:', error);
    }
  }, [
    nodes,
    edges,
    settings,
    validateNodeOptions,
    validateSettings,
    validateSensorToActionConnectivity,
    saveLogic,
    setInvalidNodeId,
    setNodes,
    setIsDirty,
  ]);

  return { handleSave, isLoading };
};
